import {
  amber,
  cream,
  dataBlue,
  dataMagenta,
  dataGround,
  dataTeal,
  green,
  ink,
  jade,
  navy,
  red,
  sand,
  sandViz,
  vermilion,
} from './reference'

/**
 * Semantic tokens: meaning, not color. Both modes answer to the same names, so
 * a component styled once is legible in either — dark mode is a re-pointing,
 * not a redesign. Values and the L* targets per role are the lab's measured
 * mapping (lab-missing-pieces.html §1: text/primary ≈ L*13, tertiary ≈ L*45,
 * canvas ≈ L*97).
 */
export type SemanticTokens = {
  /** canvas behind everything */
  bg: string
  /** raised card / paper */
  surface: string
  /** sunken panel, input wash, skeleton base */
  surface2: string
  /** text/primary */
  ink: string
  /** text/secondary */
  ink2: string
  /** text/tertiary — the darkest step that still reads as "muted" and passes 4.5:1 */
  ink3: string
  /** hairline borders and dividers */
  line: string
  /**
   * Pointer-over wash. Translucent, and drawn with the same ink as `line`, so the one
   * value reads over paper, over `surface2` and over the `selection` tint alike. An
   * opaque hover has to be re-picked per background — that is how the app grew a
   * different hover colour per surface, and why a pinned table cell that paints its
   * own background needed a second rule to be hoverable at all.
   *
   * Both modes are tuned to the same perceived step (~5.4 dE in OKLab), which is
   * where the old opaque `background.neutral` row hover sat. Dark needs less alpha
   * to get there: a light overlay on a dark ground moves further per unit.
   *
   * The weight is capped by `ink3`, the tertiary ink, which the wash sits behind.
   * At this weight it clears 4.5:1 on a hovered surface only because `sand[500]`
   * was darkened to buy the headroom — see the hover-wash tests in tokens.test.ts.
   */
  hover: string
  /** selected row / item tint. 1.05:1 by design: a checked control is the redundant cue */
  selection: string
  good: string
  goodBg: string
  danger: string
  dangerBg: string
  /** star fill — always paired with sigStroke, never meaning-bearing alone */
  star: string
  /** the darkened-amber outline that lets the star mark pass 3:1 where the fill cannot */
  sigStroke: string
  /** the same outline for an unearned mark, quiet enough that five empty stars
   * read as the outline of the shape and not as a grid of dark rings */
  sigStrokeEmpty: string
  /** focus ring */
  focus: string
}

export const semantic: { light: SemanticTokens; dark: SemanticTokens } = {
  light: {
    bg: sand[50],
    surface: sand[0],
    surface2: sand[100],
    ink: sand[900],
    ink2: sand[700],
    ink3: sand[500],
    line: 'rgba(36, 34, 28, 0.10)',
    hover: 'rgba(36, 34, 28, 0.08)',
    selection: cream[100],
    good: green[600],
    goodBg: green[100],
    danger: red[600],
    dangerBg: red[100],
    star: amber[500],
    sigStroke: amber[600],
    sigStrokeEmpty: sand[300],
    focus: navy[600],
  },
  dark: {
    bg: sand[950],
    surface: sand[925],
    surface2: sand[850],
    ink: sand[25],
    ink2: sand[300],
    ink3: sand[400],
    line: 'rgba(242, 239, 229, 0.10)',
    hover: 'rgba(242, 239, 229, 0.06)',
    selection: 'rgba(255, 201, 5, 0.10)',
    good: green[300],
    goodBg: green[950],
    danger: red[300],
    dangerBg: 'rgba(241, 152, 142, 0.12)',
    star: amber[400],
    sigStroke: amber[600],
    sigStrokeEmpty: sand[500],
    focus: '#8FAEE0',
  },
}

/**
 * Chart tokens. Every colour a chart draws comes from one of these five slots,
 * and each answers exactly one question — mixing them is what produced the four
 * unrelated colour languages the report cards used to show on one screen.
 *
 * Charts must never reach for `good`/`danger` or the amber action colour above:
 * status is reserved for state, amber is reserved for actions, and a series that
 * borrows either starts claiming a meaning it does not have.
 */
export type VizTokens = {
  /** The default mark for a single-series chart. Also categorical slot 1. */
  mark: string
  /** Ground for a context series — the mark's own background, never a second subject. */
  context: string
  /** Diverging, for every rating scale: [worst, bad, neutral, good, best]. */
  polar: readonly [string, string, string, string, string]
  /** Sequential, for magnitude (counts, volume). Light to dark. */
  volume: readonly [string, string, string, string, string]
  /**
   * Categorical, for identity. Three slots, assigned in order, never cycled.
   * Three is the ceiling, not a placeholder: amber is the action colour and
   * red/green are status, and what is left of the wheel carries three hues that
   * stay apart under colour blindness. A fourth series folds into "other" or
   * becomes a small multiple.
   */
  series: readonly [string, string, string]
}

export const viz: { light: VizTokens; dark: VizTokens } = {
  light: {
    mark: dataBlue.mark,
    context: dataGround.light,
    polar: [vermilion[700], vermilion[500], sandViz[300], jade[500], jade[700]],
    volume: [dataBlue[100], dataBlue[200], dataBlue[400], dataBlue[600], dataBlue[800]],
    series: [dataBlue.mark, dataTeal[500], dataMagenta[500]],
  },
  dark: {
    // polar is re-pointed rather than flipped: the poles move up into the dark
    // mode's lightness band while the neutral keeps its value, because it is the
    // lightest mark in both modes. volume does flip its anchor — that is the
    // correct behaviour for a sequential ramp, not a shortcut.
    mark: dataBlue.mark,
    context: dataGround.dark,
    polar: [vermilion[300], vermilion[500], sandViz[300], jade[500], jade[300]],
    volume: [dataBlue[800], dataBlue[600], dataBlue[400], dataBlue[200], dataBlue[100]],
    series: [dataBlue.mark, dataTeal[500], dataMagenta[500]],
  },
}

// ink[900] and amber[500] are referenced by the action tokens in palette.ts;
// re-export so palette has one import site for the whole token source.
export { amber, cream, green, ink, navy, red, sand }
